import React, { useEffect, useRef } from "react";
import { EnrichFieldRow } from "./EnrichFieldRow";
import { validatedFieldToEnrichedField } from "./enrichUtils";
import { EnrichedField } from "../../api/enrichApi";
import { ValidatedFieldsetField } from "../../api/fieldsetApi";

const noopSave = async (): Promise<void> => {
  /* validation rows don't save */
};
const noopEnrich = (): void => {
  /* validation rows don't enrich single field */
};

type Props = {
  sortedValidationFields: ValidatedFieldsetField[];
  enrichResults: EnrichedField[] | null;
  enrichingFieldIds: Set<number>;
  activeJobId: number | null;
  enrichFieldIds: Set<number>;
  enrichSelectableFieldIds: number[];
  onEnrichFieldIdToggle: (customerFieldId: number) => void;
  enrichSelectAllChecked: boolean;
  enrichSelectAllIndeterminate: boolean;
  onToggleAllEnrichFields: () => void;
  enrichSelectAllDisabled: boolean;
  onSaveField: (id: number, value: unknown) => Promise<void>;
  onRevertField: (id: number) => void;
  onEnrichField: (field: EnrichedField) => void;
  onEditField: (id: number) => void;
  onCreateField: (fieldKey: string) => void;
  isCreatingField: boolean;
};

export const EnrichFieldTable: React.FC<Props> = ({
  sortedValidationFields,
  enrichResults,
  enrichingFieldIds,
  activeJobId,
  enrichFieldIds,
  enrichSelectableFieldIds,
  onEnrichFieldIdToggle,
  enrichSelectAllChecked,
  enrichSelectAllIndeterminate,
  onToggleAllEnrichFields,
  enrichSelectAllDisabled,
  onSaveField,
  onRevertField,
  onEnrichField,
  onEditField,
  onCreateField,
  isCreatingField,
}) => {
  const selectAllRef = useRef<HTMLInputElement>(null);
  const selectableFieldIdSet = new Set(enrichSelectableFieldIds);

  useEffect(() => {
    const el = selectAllRef.current;
    if (el) {
      el.indeterminate = enrichSelectAllIndeterminate;
    }
  }, [enrichSelectAllIndeterminate]);

  return (
    <div className="tw-overflow-hidden tw-border-t-[1px]">
      <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
        <thead className="tw-bg-gray-50">
          <tr>
            <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-12">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={enrichSelectAllChecked}
                disabled={enrichSelectAllDisabled}
                onChange={onToggleAllEnrichFields}
                title="Select all fields for enrich"
                data-testid="enrich-field-table-select-all"
                className="tw-rounded tw-border-gray-300"
              />
            </th>
            <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-48">
              Field
            </th>
            <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-96">
              Value
            </th>
            <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-48">
              Reasoning
            </th>
            <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-48">
              Status
            </th>
            <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-24">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
          {(enrichResults ?? []).map((enrichResult) => (
            <EnrichFieldRow
              key={enrichResult.key}
              field={enrichResult}
              validationField={undefined}
              isEnrichingField={enrichingFieldIds.has(enrichResult.id)}
              disabled={!!activeJobId}
              isSelectable={
                enrichResult.customer_field_id != null &&
                selectableFieldIdSet.has(enrichResult.customer_field_id)
              }
              enrichFieldIds={enrichFieldIds}
              onEnrichFieldIdToggle={onEnrichFieldIdToggle}
              onSave={onSaveField}
              onRevert={onRevertField}
              onEnrichField={(): void => {
                void onEnrichField(enrichResult);
              }}
              onEditField={onEditField}
              onCreateField={undefined}
              isCreatingField={isCreatingField}
            />
          ))}
          {sortedValidationFields
            .filter(
              (validationField) =>
                !(enrichResults ?? []).some(
                  (r) => r.key === validationField.key
                )
            )
            .map((validationField) => (
              <EnrichFieldRow
                key={validationField.key}
                field={validatedFieldToEnrichedField(validationField)}
                validationField={validationField}
                isEnrichingField={false}
                disabled={!!activeJobId}
                isSelectable={
                  validationField.customer_field_id != null &&
                  selectableFieldIdSet.has(validationField.customer_field_id)
                }
                enrichFieldIds={enrichFieldIds}
                onEnrichFieldIdToggle={onEnrichFieldIdToggle}
                onSave={noopSave}
                onRevert={onRevertField}
                onEnrichField={noopEnrich}
                onEditField={onEditField}
                onCreateField={onCreateField}
                isCreatingField={isCreatingField}
              />
            ))}
          {sortedValidationFields.length === 0 &&
            (enrichResults ?? []).length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="tw-text-center tw-text-gray-500 tw-py-4"
                >
                  Select an Input Schema to see fields
                </td>
              </tr>
            )}
        </tbody>
      </table>
    </div>
  );
};
